import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { 
  Box, Card, CssBaseline, Typography, Grid, IconButton, Fade, Grow, 
  CircularProgress, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, Chip, Button, TablePagination,
  Dialog, DialogTitle, DialogContent
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";

// Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import LayersIcon from "@mui/icons-material/Layers";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ExtensionIcon from "@mui/icons-material/Extension";
import VisibilityIcon from "@mui/icons-material/Visibility";

// Layout & Config
import NavTab from "../../layout/NavTab";
import { fetchGetProtect } from "../../fetchs/get";
import { fetchPostProtect } from "../../fetchs/post";
import { RootState } from "../../redux/reducers/index";
import { sessionEnd, showSnackbar } from "../../redux/actions/index";

interface HistorySection {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const HISTORY_SECTIONS: HistorySection[] = [
  {
    id: "projets",
    title: "Projet",
    description: "Créations, modifications et changements de statut du projet.",
    icon: <AccountTreeIcon fontSize="inherit" />,
    color: "#3b82f6", // Blue
  },
  {
    id: "lots",
    title: "Lots",
    description: "Suivi des lots créés pour ce projet.",
    icon: <LayersIcon fontSize="inherit" />,
    color: "#8b5cf6", // Purple
  },
  {
    id: "taches",
    title: "Tâches",
    description: "Historique des tâches, incluant concepts et rôles associés.",
    icon: <AssignmentIcon fontSize="inherit" />,
    color: "#f59e0b", // Amber
  },
  {
    id: "matrice",
    title: "Matrice",
    description: "Traçabilité des envois de matrices et templates vers Dolibarr.",
    icon: <ExtensionIcon fontSize="inherit" />,
    color: "#10b981", // Emerald
  },
];

const getStatusLabel = (status: string) => {
  if (status === "0") return "Brouillon";
  if (status === "1") return "Validé";
  if (status === "2") return "Clos";
  return status || "-";
};

const ProjectHistory = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.user.token);

  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modale Visualisation Matrice
  const [matrixModal, setMatrixModal] = useState<any | null>(null);
  const [matrixModalOpen, setMatrixModalOpen] = useState(false);
  const [matrixLoading, setMatrixLoading] = useState(false);

  const handleViewMatrix = (row: any) => {
    if (!token) return;

    // Si on a le snapshot (historique figé), on l'affiche directement !
    if (row.snapshot && Array.isArray(row.snapshot) && row.snapshot.length > 0) {
      setMatrixModal({ name: row.name, data: row.snapshot });
      setMatrixModalOpen(true);
      return;
    }

    if (!row.matrixId) return;
    setMatrixLoading(true);
    setMatrixModalOpen(true);
    fetchGetProtect(token, `/matrice/id/${row.matrixId}`)
      .then(async (res) => {
        if (res.status === 200) {
          const json = await res.json();
          setMatrixModal(json?.message || json);
        } else {
          setMatrixModal(null);
        }
        setMatrixLoading(false);
      })
      .catch(() => { setMatrixModal(null); setMatrixLoading(false); });
  };

  useEffect(() => {
    if (activeSection && token && projectId) {
      setLoading(true);
      fetchGetProtect(token, `/history/project/${projectId}/items/`, activeSection)
        .then(async (res) => {
          if (res.status === 200) {
            const json = await res.json();
            setData(Array.isArray(json) ? json : []);
            setPage(0);
          } else if (res.status === 401) {
            dispatch(sessionEnd());
          } else {
            dispatch(showSnackbar("Erreur de récupération de l'historique", "error"));
          }
        })
        .finally(() => setLoading(false));
    }
  }, [activeSection, projectId, token, dispatch]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const currentData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const renderTableContent = () => {
    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
    if (!data || data.length === 0) return <Typography variant="body1" color="#94a3b8" textAlign="center" py={4}>Aucune donnée d'historique trouvée.</Typography>;

    switch(activeSection) {
      case "projets":
        return (
          <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "12px" }}>
            <Table>
              <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Auteur (Dolibarr)</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Nom</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Opp.</TableCell>
                  <TableCell>Statut</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentData.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{dayjs(row.date).format("DD/MM/YYYY HH:mm")}</TableCell>
                    <TableCell>{row.author || "-"}</TableCell>
                    <TableCell><Chip label={row.action} size="small" color={row.action === "CREATION" ? "success" : "primary"} /></TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell sx={{ maxWidth: 200 }} title={row.description}>
                      <Typography noWrap variant="body2">{row.description || "-"}</Typography>
                    </TableCell>
                    <TableCell>{row.clientName || "-"}</TableCell>
                    <TableCell>{row.opportunity || "-"}</TableCell>
                    <TableCell>{getStatusLabel(row.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      case "lots":
        return (
          <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "12px" }}>
            <Table>
              <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Compte Kairos</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Nom du lot</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentData.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{dayjs(row.date).format("DD/MM/YYYY HH:mm")}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.user}</TableCell>
                    <TableCell><Chip label={row.action} size="small" color={row.action === "CREATION" ? "success" : "primary"} /></TableCell>
                    <TableCell>{row.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      case "taches":
        return (
          <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "12px" }}>
            <Table>
              <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                <TableRow>
                  <TableCell sx={{ minWidth: 140 }}>Date</TableCell>
                  <TableCell>Compte Kairos</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Tâche & Description</TableCell>
                  <TableCell>Affecté à</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', px: 1 }}>Conc.</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', px: 1 }}>R.Dev</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', px: 1 }}>R.Inf</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', px: 1 }}>Q.Str</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', px: 1 }}>Q.CCT</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', px: 1 }}>Q.Exe</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', px: 1 }}>R.Str</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', px: 1 }}>R.CCT</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', px: 1 }}>R.Exe</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', px: 1 }}>Dep.</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', px: 1 }}>Pil&C.</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', px: 1 }}>Acc</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', px: 1 }}>Gua.</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentData.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{dayjs(row.date).format("DD/MM/YYYY HH:mm")}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.user}</TableCell>
                    <TableCell><Chip label={row.action} size="small" color={row.action === "SUPPRESSION" ? "error" : row.action === "CREATION" ? "success" : "primary"} /></TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                      {row.description && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 150 }} title={row.description}>{row.description}</Typography>}
                    </TableCell>
                    <TableCell>{row.assignedTo || "-"}</TableCell>
                    
                    {/* Colonnes Matrice */}
                    <TableCell sx={{ fontSize: '0.75rem', px: 1 }}>{row.concepts?.C || "-"}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', px: 1 }}>{row.concepts?.RTUD || "-"}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', px: 1 }}>{row.concepts?.RTUI || "-"}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', px: 1 }}>{row.concepts?.QS || "-"}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', px: 1 }}>{row.concepts?.QC || "-"}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', px: 1 }}>{row.concepts?.QE || "-"}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', px: 1 }}>{row.concepts?.RS || "-"}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', px: 1 }}>{row.concepts?.RC || "-"}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', px: 1 }}>{row.concepts?.RE || "-"}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', px: 1 }}>{row.concepts?.D || "-"}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', px: 1 }}>{row.concepts?.PC || "-"}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', px: 1 }}>{row.concepts?.A || "-"}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', px: 1 }}>{row.concepts?.G || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      case "matrice":
        return (
          <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: "12px" }}>
            <Table>
              <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Compte Kairos</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Matrice</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentData.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell>{dayjs(row.date).format("DD/MM/YYYY HH:mm")}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.user}</TableCell>
                    <TableCell><Chip label={row.action} size="small" color={row.action === "ENVOI_DOLIBARR" ? "secondary" : "primary"} /></TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        {(row.matrixId || (row.snapshot && row.snapshot.length > 0)) && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleViewMatrix(row)}
                          >
                            Voir
                          </Button>
                        )}
                        {row.snapshot && row.snapshot.length > 0 && (
                           <Button 
                             variant="contained" 
                             size="small"
                             color="warning"
                             onClick={() => {
                                if (!token) return;
                                fetchPostProtect(token, "/lots/restore-matrix-from-history", JSON.stringify({ historyMatrixId: row._id }))
                                  .then(async (res) => {
                                    if (res.status === 200) {
                                      dispatch(showSnackbar("Matrice restaurée avec succès !", "success"));
                                      navigate(`/projet/${projectId}`);
                                    } else {
                                      const json = await res.json().catch(() => ({}));
                                      dispatch(showSnackbar(json.error || "Erreur lors de la restauration de la matrice", "error"));
                                    }
                                  });
                             }}
                           >
                             Réutiliser
                           </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <CssBaseline />
      <NavTab text="Historique du Projet" />

      <Box sx={{ px: 4, py: 3, maxWidth: "1200px", width: "100%", mx: "auto" }}>
        
        {/* Header / Bouton Retour */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <IconButton 
            onClick={() => navigate(`/projet/${projectId}`)}
            sx={{ 
              bgcolor: 'white', 
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)', 
              mr: 2,
              '&:hover': { bgcolor: '#f1f5f9' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={800} color="#1e293b" letterSpacing="-0.5px">
              Centre d'Historisation
            </Typography>
            <Typography variant="body1" color="#64748b" mt={0.5}>
              Sélectionnez une catégorie pour consulter la chronologie des événements.
            </Typography>
          </Box>
        </Box>

        {/* Grille des Cartes */}
        <Grid container spacing={3}>
          {HISTORY_SECTIONS.map((sec, index) => (
            <Grow in={true} timeout={300 + index * 150} key={sec.id}>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  onClick={() => setActiveSection(sec.id)}
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    borderRadius: "20px",
                    border: activeSection === sec.id ? `2px solid ${sec.color}` : "2px solid #e2e8f0",
                    bgcolor: activeSection === sec.id ? `${sec.color}08` : "white",
                    cursor: 'pointer',
                    transition: "all 0.3s ease",
                    position: 'relative',
                    overflow: 'hidden',
                    '&:hover': {
                      transform: "translateY(-5px)",
                      boxShadow: `0 12px 24px -10px ${sec.color}60`,
                      borderColor: sec.color,
                    }
                  }}
                >
                  {/* Effet décoratif d'arrière plan */}
                  <Box 
                    sx={{
                      position: 'absolute',
                      top: '-20px',
                      right: '-20px',
                      fontSize: '120px',
                      color: sec.color,
                      opacity: 0.04,
                      transform: 'rotate(-15deg)',
                      pointerEvents: 'none'
                    }}
                  >
                    {sec.icon}
                  </Box>

                  <Box 
                    sx={{ 
                      width: 50, 
                      height: 50, 
                      borderRadius: '14px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      bgcolor: `${sec.color}15`,
                      color: sec.color,
                      fontSize: '28px',
                      mb: 2
                    }}
                  >
                    {sec.icon}
                  </Box>

                  <Typography variant="h6" fontWeight={700} color="#1e293b" mb={1}>
                    {sec.title}
                  </Typography>
                  <Typography variant="body2" color="#64748b" lineHeight={1.5}>
                    {sec.description}
                  </Typography>
                </Card>
              </Grid>
            </Grow>
          ))}
        </Grid>

        {/* Section Détails */}
        {activeSection && (
          <Fade in={Boolean(activeSection)} timeout={500}>
            <Box mt={5}>
              <Card
                elevation={0}
                sx={{
                  p: 4,
                  borderRadius: "24px",
                  border: "1px solid #e2e8f0",
                  bgcolor: "white",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.03)"
                }}
              >
                <Typography variant="h5" fontWeight={700} color="#1e293b" mb={2}>
                  Historique : {HISTORY_SECTIONS.find(s => s.id === activeSection)?.title}
                </Typography>
                
                <Box mt={3}>
                  {renderTableContent()}
                </Box>

                {!loading && data.length > 0 && (
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={data.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Lignes par page:"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} sur ${count !== -1 ? count : `plus de ${to}`}`}
                  />
                )}
              </Card>
            </Box>
          </Fade>
        )}

      </Box>

      {/* ─── Modale Visualisation Matrice (lecture seule) ─── */}
      <Dialog
        open={matrixModalOpen}
        onClose={() => { setMatrixModalOpen(false); setMatrixModal(null); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            maxHeight: '90vh',
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: '1px solid rgba(255,255,255,0.6)',
            boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          fontWeight: 800,
          fontSize: '1.2rem',
          borderBottom: '1px solid rgba(226,232,240,0.8)',
          pb: 2,
          background: 'linear-gradient(135deg, rgba(59,130,246,0.07) 0%, rgba(139,92,246,0.07) 100%)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10b981' }} />
            {matrixModal?.name || "Visualisation de la matrice"}
          </Box>
          {matrixModal?.desc && (
            <Typography variant="body2" color="text.secondary" fontWeight={400} mt={0.5} ml={3}>
              {matrixModal.desc}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {matrixLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : !matrixModal ? (
            <Typography color="text.secondary" textAlign="center" py={4}>
              Impossible de charger la matrice.
            </Typography>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{
              borderRadius: '14px',
              mt: 1,
              border: '1px solid rgba(226,232,240,0.7)',
              background: 'rgba(255,255,255,0.6)',
              backdropFilter: 'blur(8px)',
            }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, minWidth: 200, background: 'rgba(241,245,249,0.9)', color: '#475569', letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' }}>Profil</TableCell>
                    <TableCell sx={{ fontWeight: 700, background: 'rgba(241,245,249,0.9)', color: '#475569', letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' }}>Niveau</TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'right', background: 'rgba(241,245,249,0.9)', color: '#475569', letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' }}>Taux (%)</TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'right', background: 'rgba(241,245,249,0.9)', color: '#475569', letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' }}>Coût (€)</TableCell>
                    <TableCell sx={{ fontWeight: 700, textAlign: 'center', background: 'rgba(241,245,249,0.9)', color: '#475569', letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.7rem' }}>Sélectionné</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(matrixModal.data as any[])?.flatMap((unit: any) =>
                    (unit.costs as any[])?.map((cost: any, idx: number) => {
                      // isChoosen doit être STRICTEMENT true — undefined n'est pas coché
                      const isSelected = cost.isChoosen === true;
                      return (
                        <TableRow
                          key={`${unit._id}-${idx}`}
                          sx={{
                            bgcolor: isSelected ? 'rgba(16,185,129,0.06)' : 'inherit',
                            borderLeft: isSelected ? '3px solid #10b981' : '3px solid transparent',
                            transition: 'background 0.2s',
                            '&:hover': { bgcolor: isSelected ? 'rgba(16,185,129,0.10)' : 'rgba(248,250,252,0.8)' },
                          }}
                        >
                          <TableCell sx={{ fontWeight: idx === 0 ? 700 : 400, color: idx === 0 ? '#1e293b' : 'transparent', userSelect: idx === 0 ? 'auto' : 'none' }}>
                            {idx === 0 ? unit.name : '·'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={cost.level || cost.id || `—`}
                              size="small"
                              variant={isSelected ? "filled" : "outlined"}
                              sx={{
                                fontSize: '0.7rem',
                                fontWeight: isSelected ? 700 : 400,
                                bgcolor: isSelected ? 'rgba(16,185,129,0.15)' : 'transparent',
                                borderColor: isSelected ? '#10b981' : '#cbd5e1',
                                color: isSelected ? '#065f46' : '#64748b',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: isSelected ? 700 : 400, color: isSelected ? '#065f46' : '#475569' }}>
                            {cost.rate !== undefined && cost.rate !== null && cost.rate !== "" ? `${cost.rate} %` : '—'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: isSelected ? 700 : 400, color: isSelected ? '#065f46' : '#475569' }}>
                            {cost.cost !== undefined && cost.cost !== null && cost.cost !== "" ? `${cost.cost} €` : '—'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            {isSelected ? (
                              <Box sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                                bgcolor: 'rgba(16,185,129,0.12)', color: '#065f46',
                                px: 1.5, py: 0.3, borderRadius: '20px',
                                fontSize: '0.75rem', fontWeight: 700,
                                border: '1px solid rgba(16,185,129,0.3)',
                              }}>
                                ✓ Coché
                              </Box>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProjectHistory;

